// Panel inline para subir productos nuevos — capturar files (preview con
// createObjectURL), nombre opcional y confirmar. El upload real (Supabase
// + nameing IA) corre afuera; este panel solo expone callbacks.
//
// Tiene dos variants visuales:
//   • desktop → header / scroll / footer sticky (px-6 interno). Pensado
//     para vivir como vista de un side panel.
//   • mobile  → flex-col simple sin padding propio — espera que el host
//     le dé padding externo (el bottom sheet usual).
//
// Consumers: PresetDetailSheet (Choose → Upload flow) y
// MultiSlotProductPickerSheet.

import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, Plus, X } from 'lucide-react';

export interface UploadProductPanelProps {
  variant: 'mobile' | 'desktop';
  files: File[];
  name: string;
  onNameChange: (v: string) => void;
  onAddMore: (files: FileList | File[]) => void;
  onRemoveFile: (idx: number) => void;
  onCancel: () => void;
  onConfirm: () => void;
  uploading: boolean;
  error: string | null;
}

export const UploadProductPanel: React.FC<UploadProductPanelProps> = ({
  variant,
  files,
  name,
  onNameChange,
  onAddMore,
  onRemoveFile,
  onCancel,
  onConfirm,
  uploading,
  error,
}) => {
  const addMoreRef = useRef<HTMLInputElement | null>(null);

  // Previews: para cada File, generamos una URL local (createObjectURL)
  // y la liberamos al desmontar/cambiar files para no leakear memoria.
  const [previews, setPreviews] = useState<string[]>([]);
  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [files]);

  const isDesktop = variant === 'desktop';

  // El contenido de la grilla + name input es el mismo en ambos
  // variants. Sólo cambia el wrapper para sticky footer en desktop.
  const grid = (
    <div className="grid grid-cols-2 gap-2.5">
      {files.map((file, idx) => (
        <div
          key={`${file.name}-${idx}`}
          className="relative rounded-xl overflow-hidden bg-neutral-100 aspect-square ring-1 ring-black/[0.05]"
        >
          {previews[idx] && (
            <img
              src={previews[idx]}
              alt={file.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
          <button
            type="button"
            onClick={() => onRemoveFile(idx)}
            disabled={uploading}
            aria-label="Quitar imagen"
            className="absolute top-1.5 right-1.5 w-7 h-7 inline-flex items-center justify-center rounded-full bg-black/55 backdrop-blur-sm text-white hover:bg-black/70 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      {/* Tile "+" para agregar más imágenes */}
      <button
        type="button"
        onClick={() => addMoreRef.current?.click()}
        disabled={uploading}
        className="aspect-square rounded-xl border-2 border-dashed border-neutral-300 hover:border-brand-primary/40 bg-white disabled:opacity-40 disabled:cursor-not-allowed flex flex-col items-center justify-center gap-1.5 text-[#3C3C43]/55 hover:text-brand-primary transition-colors"
      >
        <Plus className="w-5 h-5" strokeWidth={2.25} />
        <span className="text-[12px] font-medium">Agregar</span>
      </button>
      <input
        ref={addMoreRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) {
            onAddMore(e.target.files);
            e.target.value = '';
          }
        }}
      />
    </div>
  );

  const nameInput = (
    <div>
      <label className="block text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-1.5">
        Nombre del producto
      </label>
      <input
        type="text"
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        placeholder="Opcional — Atlas le pone uno con IA si lo dejás vacío"
        disabled={uploading}
        className="w-full h-11 px-4 rounded-xl bg-white border border-neutral-200 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary disabled:opacity-50 transition-colors"
      />
    </div>
  );

  const errorBlock = error ? (
    <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2.5 text-[13px] text-red-700">
      {error}
    </div>
  ) : null;

  const confirmButton = (
    <button
      type="button"
      onClick={onConfirm}
      disabled={uploading || files.length === 0}
      className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[14.5px] font-semibold shadow-sm transition-colors"
    >
      {uploading ? 'Subiendo…' : 'Confirmar'}
    </button>
  );

  // ── Desktop: header / scroll / footer fijos ─────────────────────────
  if (isDesktop) {
    return (
      <div className="flex flex-col h-full">
        <div className="shrink-0 px-6 pt-6 pb-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={uploading}
            className="inline-flex items-center gap-1 text-brand-primary text-[13px] mb-3 hover:opacity-75 disabled:opacity-40 transition-opacity"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Volver
          </button>
          <p className="text-[16px] font-semibold text-brand-black/90">
            Nuevo producto
          </p>
          <p className="text-[12.5px] text-[#3C3C43]/55 mt-0.5">
            Sumá más imágenes o ponele un nombre antes de confirmar.
          </p>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 space-y-4 pb-3">
          {grid}
          {nameInput}
          {errorBlock}
        </div>
        <div className="shrink-0 px-6 pt-3 pb-6 border-t border-black/[0.05] bg-white">
          {confirmButton}
        </div>
      </div>
    );
  }

  // ── Mobile: inline con padding del wrapper externo ──────────────────
  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        onClick={onCancel}
        disabled={uploading}
        className="inline-flex items-center gap-1 text-brand-primary text-[13px] hover:opacity-75 disabled:opacity-40 transition-opacity self-start"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
        Volver
      </button>
      <div>
        <p className="text-[16px] font-semibold text-brand-black/90">
          Nuevo producto
        </p>
        <p className="text-[12.5px] text-[#3C3C43]/55 mt-0.5">
          Sumá más imágenes o ponele un nombre antes de confirmar.
        </p>
      </div>
      {grid}
      {nameInput}
      {errorBlock}
      {confirmButton}
    </div>
  );
};
